import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import Image from 'components/Common/Image'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { formatFlightDealTravelPeriods } from './formatFlightDealTravelPeriods'

interface Props {
  activeDeal: App.FlightDeal
  showCheckAvailabilityCta?: boolean
  onCheckAvailability?: () => void
}

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`

const LabelOverlay = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-start: ${rem(8)};
  z-index: 1;
`

function FlightDealModal(props: Props) {
  const { activeDeal, showCheckAvailabilityCta, onCheckAvailability } = props
  const intl = useIntl()
  const { fareTypeLabelMap } = useFlightFareLabels()

  const travelPeriodsText = useMemo(() => {
    if (!activeDeal?.travelPeriods) return ''
    return formatFlightDealTravelPeriods(activeDeal.travelPeriods)
  }, [activeDeal?.travelPeriods])

  const flightSaleLabel = intl.formatMessage({
    id: '1ZxZrOONDKHC8Z3PLAkR',
    defaultMessage: 'Flight sale',
    description: 'Flight deal urgency label on offer page banner when countdown is hidden',
  })

  const fromPricePerAdult = intl.formatMessage(
    {
      id: 'HKHIshendNVHZZ_WWg4M',
      defaultMessage: 'From {price}/adult',
      description: 'Price display showing the starting price per adult for flight search result cards',
    },
    {
      price: (
        <strong>
          <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />
        </strong>
      ),
    },
  )

  const travelOnSelectDatesBetween = intl.formatMessage({
    id: 'FVwJgj7pMKEZS6gfXI74',
    defaultMessage: 'Travel on select dates between',
    description: 'Heading for travel period dates in flight deal modal',
  })

  const termsAndConditionsHeading = intl.formatMessage({
    id: 'dgqOQupRN8YbSyH4qUOI',
    defaultMessage: 'Terms & Conditions',
    description: 'Modal title for terms and conditions in the flights section',
  })

  const checkAvailabilityLabel = intl.formatMessage({
    id: '7R2gXK8MsBe5_PKUubtN',
    defaultMessage: 'Check availability',
    description: 'Action button label to check availability in the flights section',
  })

  const checkAvailabilityActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: checkAvailabilityLabel,
    onClick: (_event, resolve) => {
      onCheckAvailability?.()
      resolve()
    },
  }), [checkAvailabilityLabel, onCheckAvailability])

  const flightDealSummary = intl.formatMessage(
    {
      id: 'EM56NGZFjTO6em5aUcQX',
      defaultMessage: '{carrierName} {origin}-{destination} {fareType} {price}',
      description: 'Flight deal summary showing carrier name, route, fare type, and price per adult',
    },
    {
      carrierName: activeDeal.carrier.name,
      origin: activeDeal.originAirportCode,
      destination: activeDeal.destinationAirportCode,
      fareType: fareTypeLabelMap[activeDeal.fareType],
      price: fromPricePerAdult,
    },
  )

  return (
    <ModalBase modalId="flight-deal-modal">
      <ModalHeader title={activeDeal.dealName} />
      <ModalBody>
        {activeDeal.heroImage && (
          <ImageContainer>
            <ModalImageThumbnail image={activeDeal.heroImage} imageParams={{ aspectRatio: '3.15' }} />
            <LabelOverlay>
              <UrgencyLabel icon={<SolidFireIcon />} variant="speed">
                {flightSaleLabel}
              </UrgencyLabel>
            </LabelOverlay>
          </ImageContainer>
        )}
        <ModalContent>
          <VerticalSpacer gap={20}>
            <BodyTextBlock
              startIcon={activeDeal.carrier.logo ? <Image image={activeDeal.carrier.logo} height={24} /> : undefined}
              variant="medium"
            >
              {flightDealSummary}
            </BodyTextBlock>

            {travelPeriodsText && (
              <VerticalSpacer gap={4}>
                <BodyText variant="large" weight="bold">{travelOnSelectDatesBetween}</BodyText>
                <BodyTextBlock startIcon={<LineCalendarIcon />} variant="medium">
                  {travelPeriodsText}
                </BodyTextBlock>
              </VerticalSpacer>
            )}

            {activeDeal.termsAndConditions && (
              <div>
                <Caption variant="small" weight="bold">{termsAndConditionsHeading}</Caption>
                <Caption variant="small" weight="normal">{activeDeal.termsAndConditions}</Caption>
              </div>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {showCheckAvailabilityCta && (
        <ModalFooter
          primaryActionProps={checkAvailabilityActionProps}
          actionsLayout="stacked"
        />
      )}
    </ModalBase>
  )
}

export default FlightDealModal
